// The §12.4 mouth atlas: one CanvasTexture of 6 expressions drawn with Canvas 2D at boot, laid
// out as 3 columns × 2 rows. The body shader projects a cell onto the face (a decal on the lathe,
// so the mouth always hugs the body and never needs sorting).
import { CanvasTexture, LinearFilter, LinearMipmapLinearFilter, SRGBColorSpace, type Texture } from 'three';
import { INK } from '../materials/library';

/** Expression ids = atlas cells (§12.4 order). */
export const EXPR = { SMILE: 0, O: 1, GRIMACE: 2, ZIGZAG: 3, SMUG: 4, SAD: 5 } as const;
export type Expr = (typeof EXPR)[keyof typeof EXPR];

const CELL_W = 288;
const CELL_H = 192;
const MOUTH_DARK = '#4A1630';
const TONGUE = '#FF6F8E';
const TEETH = '#FFFFFF';

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function createCanvas(w: number, h: number): HTMLCanvasElement | OffscreenCanvas | null {
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  return null;
}

function ink(ctx: Ctx, width: number): void {
  ctx.strokeStyle = INK;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
}

/** An open mouth: dark interior with a tongue, clipped to the given path, then inked. */
function openMouth(ctx: Ctx, path: (c: Ctx) => void, tongueY: number, tongueR: number): void {
  ctx.save();
  ctx.beginPath();
  path(ctx);
  ctx.closePath();
  ctx.fillStyle = MOUTH_DARK;
  ctx.fill();
  ctx.clip();
  ctx.fillStyle = TONGUE;
  ctx.beginPath();
  ctx.ellipse(0, tongueY, tongueR, tongueR * 0.62, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.beginPath();
  path(ctx);
  ctx.closePath();
  ink(ctx, 13);
  ctx.stroke();
}

const DRAW: Readonly<Record<Expr, (ctx: Ctx) => void>> = {
  [EXPR.SMILE]: (ctx) => {
    openMouth(ctx, (c) => {
      c.moveTo(-70, -22);
      c.quadraticCurveTo(0, -8, 70, -22);
      c.bezierCurveTo(62, 40, 26, 62, 0, 62);
      c.bezierCurveTo(-26, 62, -62, 40, -70, -22);
    }, 52, 34);
    ink(ctx, 11);
    ctx.beginPath();
    ctx.arc(-80, -26, 10, Math.PI * 0.75, Math.PI * 1.35);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(80, -26, 10, -Math.PI * 0.35, Math.PI * 0.25);
    ctx.stroke();
  },
  [EXPR.O]: (ctx) => {
    openMouth(ctx, (c) => c.ellipse(0, 6, 40, 58, 0, 0, Math.PI * 2), 50, 28);
  },
  [EXPR.GRIMACE]: (ctx) => {
    const w = 78;
    const h = 30;
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(-w, -h, w * 2, h * 2, 22);
    ctx.fillStyle = TEETH;
    ctx.fill();
    ctx.clip();
    ink(ctx, 7);
    ctx.beginPath();
    ctx.moveTo(-w, 0);
    ctx.lineTo(w, 0);
    for (let x = -w + 26; x < w; x += 26) {
      ctx.moveTo(x, -h);
      ctx.lineTo(x, h);
    }
    ctx.stroke();
    ctx.restore();
    ink(ctx, 12);
    ctx.beginPath();
    ctx.roundRect(-w, -h, w * 2, h * 2, 22);
    ctx.stroke();
  },
  [EXPR.ZIGZAG]: (ctx) => {
    ink(ctx, 14);
    ctx.beginPath();
    const n = 6;
    for (let i = 0; i <= n; i++) {
      const x = -84 + (168 * i) / n;
      const y = i % 2 === 0 ? 14 : -16;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  },
  [EXPR.SMUG]: (ctx) => {
    ink(ctx, 15);
    ctx.beginPath();
    ctx.moveTo(-60, 10);
    ctx.bezierCurveTo(-20, 34, 30, 30, 66, -24);
    ctx.stroke();
    ink(ctx, 10);
    ctx.beginPath();
    ctx.moveTo(56, -36);
    ctx.lineTo(76, -12);
    ctx.stroke();
  },
  [EXPR.SAD]: (ctx) => {
    openMouth(ctx, (c) => {
      c.moveTo(-54, 34);
      c.bezierCurveTo(-40, -30, 40, -30, 54, 34);
      c.quadraticCurveTo(0, 20, -54, 34);
    }, 30, 22);
  },
};

let atlas: Texture | null | undefined;

/** The shared atlas (null outside a browser, e.g. unit tests: faces are then drawn blank). */
export function mouthAtlas(): Texture | null {
  if (atlas !== undefined) return atlas;
  const canvas = createCanvas(CELL_W * 3, CELL_H * 2);
  const ctx = canvas?.getContext('2d') as Ctx | null | undefined;
  if (!canvas || !ctx) {
    atlas = null;
    return atlas;
  }
  for (let e = 0; e < 6; e++) {
    ctx.save();
    ctx.translate((e % 3) * CELL_W + CELL_W / 2, Math.floor(e / 3) * CELL_H + CELL_H / 2);
    DRAW[e as Expr](ctx);
    ctx.restore();
  }
  const tex = new CanvasTexture(canvas as HTMLCanvasElement);
  tex.colorSpace = SRGBColorSpace;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 4;
  atlas = tex;
  return atlas;
}
